import { ComponentType } from "react";
import { Message, ToolActivity } from "common/utils/chatAgent/types";
import { Markdown } from "../../Common/__styles__/Markdown";
import { Icon as LucideIcon } from "../../Common/Icons/LucideIcons";
import {
  AssistantHeader,
  AssistantName,
  HistoryContainer,
  LoadingSpinnerContainer,
  MessageContainer,
  MessageContent,
  UserMessageBubble,
  UserMessageContainer,
  UserMessageText,
} from "./__styles__/ChatHistory";
import FileReferences from "./FileReferences";
import LoadingSpinner from "./LoadingSpinner";
import ReasoningCard from "./ReasoningCard";
import ThinkingIndicator from "./ThinkingIndicator";
import FormToolActivityCard from "./toolCards/FormToolActivityCard";

export type ChatHistoryProps = {
  messages: Message[];
  isLoading?: boolean;
  ToolActivityCardComponent?: ComponentType<{ activity: ToolActivity }>;
  thinkingPhrases?: string[];
  workingPhrases?: string[];
};

const ChatHistory = ({
  messages,
  isLoading = false,
  ToolActivityCardComponent = FormToolActivityCard,
  thinkingPhrases,
  workingPhrases,
}: ChatHistoryProps) => {
  const showLoading =
    isLoading &&
    (messages.length === 0 || messages[messages.length - 1]?.role === "user");

  const ThinkingLoadingIndicator =
    thinkingPhrases ?
      <ThinkingIndicator phrases={thinkingPhrases} />
    : <LoadingSpinner />;

  const WorkingLoadingIndicator =
    workingPhrases ?
      <ThinkingIndicator phrases={workingPhrases} />
    : <LoadingSpinner />;

  const shouldShowInlineLoading = (
    messageIndex: number,
    message: Message,
  ): boolean => {
    if (!isLoading) return false;
    if (messageIndex !== messages.length - 1) return false;
    if (message.role !== "assistant") return false;
    if (message.parts.length === 0) return false; // Handled by the empty state

    const lastPart = message.parts[message.parts.length - 1];
    return lastPart?.type === "tool-activity";
  };

  return (
    <HistoryContainer>
      {messages.map((message, messageIndex) => (
        <MessageContainer key={message.id}>
          {message.role === "assistant" ?
            <>
              <AssistantHeader>
                <LucideIcon
                  iconName="sparkles"
                  color="contentPrimary"
                  size={14}
                />
                <AssistantName>Assistant</AssistantName>
              </AssistantHeader>
              <MessageContent>
                {message.parts.length === 0 && isLoading ?
                  <LoadingSpinnerContainer>
                    {ThinkingLoadingIndicator}
                  </LoadingSpinnerContainer>
                : <>
                    {message.parts.map((part, i: number) => {
                      if (part.type === "text") {
                        return (
                          <Markdown
                            key={i}
                            markdown={part.text}
                            css={{
                              fontSize: "14px",
                              fontFamily: "Inter",
                              lineHeight: "20px",
                              color: "#0C1629",
                              p: {
                                margin: 0,
                                fontSize: "14px",
                                fontFamily: "Inter",
                                lineHeight: "20px",
                                color: "#0C1629",
                              },
                            }}
                          />
                        );
                      }
                      if (part.type === "tool-activity") {
                        return (
                          <ToolActivityCardComponent
                            key={part.activity.toolCallId}
                            activity={part.activity}
                          />
                        );
                      }
                      if (part.type === "reasoning") {
                        const isLastMessage =
                          messageIndex === messages.length - 1;
                        const hasTextPart = message.parts.some(
                          p => p.type === "text",
                        );
                        const shouldShowReasoningLoading =
                          isLoading && isLastMessage && !hasTextPart;
                        return (
                          <ReasoningCard
                            key={`reasoning-${i}`}
                            reasoningPart={part}
                            isLoading={shouldShowReasoningLoading}
                          />
                        );
                      }
                      return null;
                    })}
                    {shouldShowInlineLoading(messageIndex, message) && (
                      <LoadingSpinnerContainer>
                        {WorkingLoadingIndicator}
                      </LoadingSpinnerContainer>
                    )}
                  </>
                }
              </MessageContent>
            </>
          : message.role === "user" ?
            <UserMessageContainer>
              <FileReferences parts={message.parts} />
              <UserMessageBubble>
                <UserMessageText>
                  {message.parts.map(part => {
                    if (part.type === "text") return part.text;
                    return null;
                  })}
                </UserMessageText>
              </UserMessageBubble>
            </UserMessageContainer>
          : null}
        </MessageContainer>
      ))}
      {showLoading && (
        <MessageContainer>
          <AssistantHeader>
            <LucideIcon
              iconName="sparkles"
              color="contentPrimary"
              size={14}
              testId="assistant-sparkles-icon"
            />
            <AssistantName data-testid="assistant-name">
              Assistant
            </AssistantName>
          </AssistantHeader>
          <MessageContent>
            <LoadingSpinnerContainer>
              {ThinkingLoadingIndicator}
            </LoadingSpinnerContainer>
          </MessageContent>
        </MessageContainer>
      )}
    </HistoryContainer>
  );
};

export default ChatHistory;
